import type { NavigationGuardNext, RouteLocationNormalized } from 'vue-router'
import { useAuthStore } from '@/stores/auth'
import { useNotificationStore } from '@/stores/notification'

/**
 * Determina qual página de login usar baseado na rota de destino
 */
function getLoginRoute(to: RouteLocationNormalized) {
  // Se a rota é do módulo /usuario, usar a página de login específica
  if (to.path.startsWith('/usuario')) {
    return { name: 'UsuarioLogin', query: { redirect: to.fullPath } }
  }
  // Caso contrário, usar a página de login genérica
  return { name: 'Login', query: { redirect: to.fullPath } }
}

/**
 * Guard de Roles
 * Verifica se o usuário tem uma das roles permitidas para acessar a rota
 */
export const roleGuard = (allowedRoles: string[]) => {
  return async (
    to: RouteLocationNormalized,
    from: RouteLocationNormalized,
    next: NavigationGuardNext
  ) => {
    const authStore = useAuthStore()
    const notificationStore = useNotificationStore()

    try {
      if (!authStore.user) {
        notificationStore.showError('Usuário não encontrado.')
        next(getLoginRoute(to))
        return
      }

      const userRole = authStore.user.role
      const hasRole = allowedRoles.includes(userRole)

      if (!hasRole) {
        notificationStore.showError('Você não tem permissão para acessar esta página.')
        next({ name: 'Dashboard' })
        return
      }

      next()
    } catch (error) {
      console.error('Erro no guard de roles:', error)
      notificationStore.showError('Erro na verificação de roles.')
      next({ name: 'Dashboard' })
    }
  }
}

/**
 * Guard de Role Única
 * Verifica se o usuário tem uma role específica
 */
export const singleRoleGuard = (role: string) => {
  return roleGuard([role])
}

/**
 * Guard de Admin
 * Verifica se o usuário é admin ou super_admin
 */
export const adminGuard = roleGuard(['admin', 'super_admin'])

/**
 * Guard de Super Admin
 * Verifica se o usuário é super_admin
 */
export const superAdminGuard = singleRoleGuard('super_admin')